import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Send } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TARGETS = [
    { id: 'all',     label: 'All players',  hint: 'Every customer account' },
    { id: 'active',  label: 'Active only',  hint: 'Excludes suspended / banned' },
    { id: 'vip',     label: 'VIP+ (silver+)', hint: 'VIP level 2 and above' },
    { id: 'new_48h', label: 'New (48h)',    hint: 'Signed up in the last 48 hours' },
];

/**
 * One-click broadcast to a cohort. Uses the existing notification system —
 * each recipient gets an in-app notification + push (if subscribed).
 */
export const BroadcastMessagePanel = () => {
    const [target, setTarget] = useState('all');
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const send = async () => {
        if (!title.trim() || !message.trim()) {
            toast.error('Title and message are required');
            return;
        }
        const targetLabel = TARGETS.find(t => t.id === target)?.label || 'recipients';
        if (!window.confirm(`Send "${title}" to ${targetLabel}? This cannot be undone.`)) return;

        setSending(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/message/broadcast`,
                { target, title: title.trim(), message: message.trim() },
                { withCredentials: true },
            );
            toast.success(`Broadcast sent to ${data.count} player${data.count === 1 ? '' : 's'}`);
            setTitle(''); setMessage('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send broadcast');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="card-dark p-6" data-testid="broadcast-panel">
            <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-[#FFD700]" />
                Broadcast Message
            </h3>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Push an in-app notification to a group of players. Useful for promos, schedule changes, jackpot announcements.
            </p>

            <div className="space-y-4">
                <div>
                    <Label className="text-white text-sm">Recipients</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {TARGETS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTarget(t.id)}
                                title={t.hint}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                                    target === t.id
                                        ? 'bg-[#FFD700] text-black'
                                        : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10 hover:text-white'
                                }`}
                                data-testid={`broadcast-target-${t.id}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <Label className="text-white text-sm">Title</Label>
                    <Input
                        value={title}
                        maxLength={80}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Jackpot tonight!"
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="broadcast-title"
                    />
                </div>
                <div>
                    <Label className="text-white text-sm">Message</Label>
                    <textarea
                        value={message}
                        maxLength={500}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="What you want to broadcast…"
                        rows={4}
                        className="mt-1 w-full px-3 py-2 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#FFD700] focus:outline-none focus:ring-1 focus:ring-[#FFD700]/30 resize-none"
                        data-testid="broadcast-body"
                    />
                    <p className="text-[10px] text-[#A0AAB2] mt-1 text-right tabular-nums">{message.length} / 500</p>
                </div>

                <Button
                    onClick={send}
                    disabled={sending || !title.trim() || !message.trim()}
                    className="w-full bg-[#FFD700] text-black hover:bg-[#FFC700] font-bold"
                    data-testid="broadcast-send"
                >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Sending…' : 'Send Broadcast'}
                </Button>
            </div>
        </div>
    );
};
